import { CalendarDays, Frown, Hand, MapPin } from 'lucide-react';
import {
  Button,
  Chip,
  Description,
  EmptyState,
  Heading,
  Table,
} from '@heroui/react';
import { hasFloorPlanLocation } from '../../../components/floor-plan/floor-plan-utils';
import type { AvailableSpot } from '../../../api/available-spots';
import { DateTimeFrame } from '../../../components/ui/datetime-frame';
import { ParkingSpotCard } from './parking-spot-card';

interface AvailableSpotsSectionProps {
  spots: AvailableSpot[];
  onRequest: (spot: AvailableSpot) => void;
  onViewLocation: (spot: AvailableSpot) => void;
  onViewAvailability: (spot: AvailableSpot) => void;
}

function formatBlockLabel(spot: AvailableSpot): string {
  if (!spot.blockName) return '—';
  return spot.blockCode ? `${spot.blockName} (${spot.blockCode})` : spot.blockName;
}

function formatLocation(spot: AvailableSpot): string {
  return [spot.level, spot.location].filter(Boolean).join(' · ') || '—';
}

function SpotPeriodRange({
  spot,
  onViewAvailability,
}: {
  spot: AvailableSpot;
  onViewAvailability: (spot: AvailableSpot) => void;
}) {
  const first = spot.availabilityWindows[0];
  if (!first) {
    return <Description className="text-sm">Sem período informado</Description>;
  }

  const hasMultiplePeriods = spot.availabilityWindows.length > 1;

  if (hasMultiplePeriods) {
    const periodCount = spot.availabilityWindows.length;

    return (
      <div className="flex flex-col gap-4 rounded-2xl bg-accent/10 px-4 py-4">
        <div className="flex items-start gap-3">
          <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent">
            <CalendarDays size={22} />
          </div>
          <div className="min-w-0">
            <Heading level={3} className="text-base font-semibold leading-snug">
              Há mais de um período disponível
            </Heading>
            <Description className="mt-1">
              {periodCount} janelas abertas nesta vaga. Escolha o horário que funciona para você.
            </Description>
          </div>
        </div>
        <Button
          variant="secondary"
          className="w-full"
          onPress={() => onViewAvailability(spot)}
        >
          <CalendarDays size={16} />
          Ver disponibilidade
        </Button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4">
      <DateTimeFrame label="Início" iso={first.startAt} />
      <DateTimeFrame label="Fim" iso={first.endAt} />
    </div>
  );
}

function sortOthersFirst(spots: AvailableSpot[]): AvailableSpot[] {
  return [...spots].sort((a, b) => Number(!!a.isMine) - Number(!!b.isMine));
}

export function AvailableSpotsSection({
  spots,
  onRequest,
  onViewLocation,
  onViewAvailability,
}: AvailableSpotsSectionProps) {
  if (spots.length === 0) {
    return (
      <EmptyState className="flex flex-col items-center gap-2 py-12 text-center">
        <Frown size={40} className="text-muted" />
        <Heading level={3}>Nenhuma vaga disponível</Heading>
        <Description>Ajuste os filtros ou tente solicitar em massa abaixo.</Description>
      </EmptyState>
    );
  }

  const orderedSpots = sortOthersFirst(spots);

  return (
    <>
      <div className="hidden md:block overflow-x-auto">
        <Table aria-label="Vagas disponíveis">
          <Table.ScrollContainer>
            <Table.Content>
              <Table.Header>
                <Table.Column isRowHeader>Código</Table.Column>
                <Table.Column>Bloco</Table.Column>
                <Table.Column>Local</Table.Column>
                <Table.Column>Períodos</Table.Column>
                <Table.Column className="w-56 text-right">Ação</Table.Column>
              </Table.Header>
              <Table.Body items={orderedSpots}>
                {(spot) => (
                  <Table.Row id={spot.id}>
                    <Table.Cell>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-medium">{spot.code}</span>
                        {spot.isMine ? (
                          <Chip size="sm" variant="soft" color="accent">
                            Minha
                          </Chip>
                        ) : null}
                      </div>
                    </Table.Cell>
                    <Table.Cell className="text-muted">{formatBlockLabel(spot)}</Table.Cell>
                    <Table.Cell className="text-muted">{formatLocation(spot)}</Table.Cell>
                    <Table.Cell>
                      <SpotPeriodRange spot={spot} onViewAvailability={onViewAvailability} />
                    </Table.Cell>
                    <Table.Cell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {hasFloorPlanLocation(spot) ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            onPress={() => onViewLocation(spot)}
                          >
                            <MapPin size={14} />
                            Ver no mapa
                          </Button>
                        ) : null}
                        <Button
                          size="sm"
                          variant={spot.isMine ? 'secondary' : 'primary'}
                          isDisabled={!!spot.isMine}
                          onPress={() => onRequest(spot)}
                        >
                          <Hand size={14} />
                          {spot.isMine ? 'Sua vaga' : 'Solicitar'}
                        </Button>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table.Content>
          </Table.ScrollContainer>
        </Table>
      </div>

      <div className="flex flex-col gap-3 md:hidden">
        {orderedSpots.map((spot, index) => (
          <ParkingSpotCard
            key={spot.id}
            isTourTarget={index === 0}
            code={spot.code}
            description={`${formatBlockLabel(spot)} · ${formatLocation(spot)}`}
            chipLabel={spot.isMine ? 'Minha' : 'Disponível'}
            chipColor={spot.isMine ? 'accent' : 'success'}
            onViewLocation={
              hasFloorPlanLocation(spot) ? () => onViewLocation(spot) : undefined
            }
            primaryAction={{
              label: spot.isMine ? 'Sua vaga' : 'Solicitar uso',
              onPress: () => onRequest(spot),
              isDisabled: !!spot.isMine,
              variant: spot.isMine ? 'secondary' : 'primary',
            }}
          >
            <SpotPeriodRange spot={spot} onViewAvailability={onViewAvailability} />
          </ParkingSpotCard>
        ))}
      </div>
    </>
  );
}
